/**
 * SliceSlot - Represents a slot for joining two intersecting planes
 *
 * In waffle construction, slots are cuts made in each plane at intersection points
 * to allow the planes to fit together. This class represents the geometry and
 * parameters of a single slot.
 *
 * Ported from cc.sketchchair.sketch.SliceSlot.java
 * Simplified for Phase 3 - focuses on core slot geometry
 */

import * as THREE from 'three';
import { Vec2D } from '../sketch/geometry/Vec2D.js';
import { SliceSlotType } from './SliceSlotType.js';

export class SliceSlot {
    // Re-export enum as static properties for backward compatibility
    static SLOT = SliceSlotType.SLOT;
    static FINGER = SliceSlotType.FINGER;
    static SLOTS_AND_FINGER = SliceSlotType.SLOTS_AND_FINGER;

    /**
     * Create a new SliceSlot
     * @param {Object} options - Configuration options
     */
    constructor(options = {}) {
        const {
            plane = null,          // The SlicePlane this slot is on
            position = null,       // 2D position on the plane (Vec2D)
            direction = null,      // 2D direction vector (Vec2D)
            length = 10.0,         // Length of the slot in mm
            fingerLength = 10.0,   // Length of finger sections (for finger joints)
            width = 5.0,           // Width of the slot (material thickness)
            numSlots = 1,          // Number of alternating slots (for finger joints)
            startOnSlot = true,    // Whether to start with slot or finger
            type = SliceSlotType.SLOT, // Slot type
            onEdge = false,        // Whether slot is on the edge of the shape
            percent = 0            // Position along spline (0 to 1)
        } = options;

        // Core properties
        this.plane = plane;
        this.position = position ? position.clone() : new Vec2D(0, 0);
        this.direction = direction ? direction.clone() : new Vec2D(0, 1);
        this.length = length;
        this.fingerLength = fingerLength;
        this.width = width;
        this.numSlots = numSlots;
        this.startOnSlot = startOnSlot;
        this.type = type;
        this.onEdge = onEdge;
        this.percent = percent;

        // Additional properties
        this.fingerTolerance = 0.0; // Extra space for easier assembly
        this.destroy = false;       // Mark for deletion
        this.makesEdge = true;      // Creates edge in final geometry (default true per spec)
        this.tiedToLeg = false;     // Priority in collision detection (leg slots never removed)
    }

    /**
     * Get the outline of this slot as a rectangular path
     * @param {number} offset - Offset along direction from position
     * @param {number} slotLength - Length of this particular slot section
     * @param {number} extendLength - Extension beyond nominal length
     * @returns {Array<Vec2D>} Array of corner points
     */
    getOutline(offset = 0, slotLength = null, extendLength = 0) {
        if (slotLength === null) {
            slotLength = this.length;
        }

        // Adjust for extension
        if (slotLength > 0) {
            extendLength = -extendLength;
        }

        const tempWidth = this.width;
        const tempLength = slotLength + (this.fingerTolerance * 2) - (extendLength * 2);

        // Calculate 4 corners of the rectangular slot
        // Starting from position, going in direction with width perpendicular

        // Perpendicular to direction (rotated 90 degrees)
        const perp = new Vec2D(-this.direction.y, this.direction.x);
        perp.normalize();

        // Start point with offset
        const startPoint = this.position.clone().add(
            this.direction.clone().scale(offset + extendLength)
        );

        // Four corners
        const corner1 = startPoint.clone().add(perp.clone().scale(tempWidth / 2));
        const corner2 = startPoint.clone().add(perp.clone().scale(-tempWidth / 2));

        const endPoint = this.position.clone().add(
            this.direction.clone().scale(offset + tempLength + extendLength)
        );

        const corner3 = endPoint.clone().add(perp.clone().scale(-tempWidth / 2));
        const corner4 = endPoint.clone().add(perp.clone().scale(tempWidth / 2));

        return [corner1, corner2, corner3, corner4];
    }

    /**
     * Get all outlines for this slot (including finger joints if applicable)
     * @returns {Array<Array<Vec2D>>} Array of outline arrays
     */
    getAllOutlines() {
        const outlines = [];

        if (this.type === SliceSlotType.SLOT) {
            // Simple single slot
            outlines.push(this.getOutline(0, this.length));
        } else if (this.type === SliceSlotType.SLOTS_AND_FINGER || this.type === SliceSlotType.FINGER) {
            // Alternating slots and fingers
            let slotOffset = 0;

            for (let i = 1; i <= this.numSlots; i++) {
                const isEven = (i % 2 === 0);
                const shouldAddSlot = (isEven && !this.startOnSlot) || (!isEven && this.startOnSlot);

                if (shouldAddSlot) {
                    outlines.push(this.getOutline(slotOffset, this.length));
                    slotOffset += this.length;
                } else {
                    slotOffset += this.fingerLength;
                }
            }
        }

        return outlines;
    }

    /**
     * Get 3D world position of this slot
     * @returns {THREE.Vector3|null} World position
     */
    getWorldPosition() {
        if (!this.plane) return null;
        return this.plane.getWorldPos(this.position);
    }

    /**
     * Get 3D world direction of this slot
     * @returns {THREE.Vector3|null} World direction
     */
    getWorldDirection() {
        if (!this.plane) return null;

        // Convert 2D direction to 3D on the plane
        const endPoint = this.position.clone().add(this.direction);
        const worldStart = this.plane.getWorldPos(this.position);
        const worldEnd = this.plane.getWorldPos(endPoint);

        return new THREE.Vector3()
            .subVectors(worldEnd, worldStart)
            .normalize();
    }

    /**
     * Swap the direction of this slot (flip 180 degrees)
     * Used when determining which side of the plane the slot should be on
     */
    swap() {
        // Move position to end of slot
        this.position.addSelf(this.direction.clone().scale(this.length));
        // Reverse direction
        this.direction.scale(-1);
    }

    /**
     * Check if this slot collides with another slot
     * @param {SliceSlot} other - Other slot to check
     * @returns {boolean} True if slots collide
     */
    checkCollision(other) {
        // Get corners of this slot
        const corners1 = this.getOutline(0, this.length);

        // Get corners of other slot
        const corners2 = other.getOutline(0, other.length);

        // Simple AABB (axis-aligned bounding box) collision check
        // This is a simplified version - the Java code does more complex polygon intersection

        const minX1 = Math.min(...corners1.map(c => c.x));
        const maxX1 = Math.max(...corners1.map(c => c.x));
        const minY1 = Math.min(...corners1.map(c => c.y));
        const maxY1 = Math.max(...corners1.map(c => c.y));

        const minX2 = Math.min(...corners2.map(c => c.x));
        const maxX2 = Math.max(...corners2.map(c => c.x));
        const minY2 = Math.min(...corners2.map(c => c.y));
        const maxY2 = Math.max(...corners2.map(c => c.y));

        return !(maxX1 < minX2 || maxX2 < minX1 || maxY1 < minY2 || maxY2 < minY1);
    }

    /**
     * Clone this slot
     * @returns {SliceSlot} Cloned slot
     */
    clone() {
        return new SliceSlot({
            plane: this.plane,
            position: this.position.clone(),
            direction: this.direction.clone(),
            length: this.length,
            fingerLength: this.fingerLength,
            width: this.width,
            numSlots: this.numSlots,
            startOnSlot: this.startOnSlot,
            type: this.type,
            onEdge: this.onEdge,
            percent: this.percent
        });
    }

    /**
     * Export to JSON
     * @returns {Object} JSON representation
     */
    toJSON() {
        return {
            position: { x: this.position.x, y: this.position.y },
            direction: { x: this.direction.x, y: this.direction.y },
            length: this.length,
            fingerLength: this.fingerLength,
            width: this.width,
            numSlots: this.numSlots,
            startOnSlot: this.startOnSlot,
            type: this.type,
            onEdge: this.onEdge,
            percent: this.percent,
            fingerTolerance: this.fingerTolerance
        };
    }

    /**
     * Load from JSON
     * @param {Object} data - JSON data
     * @param {SlicePlane} plane - Plane this slot is on
     * @returns {SliceSlot} New SliceSlot instance
     */
    static fromJSON(data, plane = null) {
        return new SliceSlot({
            plane: plane,
            position: new Vec2D(data.position.x, data.position.y),
            direction: new Vec2D(data.direction.x, data.direction.y),
            length: data.length,
            fingerLength: data.fingerLength,
            width: data.width,
            numSlots: data.numSlots,
            startOnSlot: data.startOnSlot,
            type: data.type,
            onEdge: data.onEdge,
            percent: data.percent
        });
    }

    /**
     * Get string representation
     * @returns {string} Description
     */
    toString() {
        return `SliceSlot[pos=(${this.position.x.toFixed(1)}, ${this.position.y.toFixed(1)}), dir=(${this.direction.x.toFixed(2)}, ${this.direction.y.toFixed(2)}), len=${this.length.toFixed(1)}]`;
    }
}
